<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="[]"
    ref="gc-base-layout"
    class="crop-tracker-layout"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Create New Crop Tracker'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          baseView.mode === 'create'
            ? 'Create New Crop Tracker'
            : 'Crop Tracker # ' + baseView.id
        "
        :secondaryLabel="undefined"
      />
    </template>

    <div class="css-v2 transaction-screen">
      <!-- Progress Banner -->
      <div v-if="IsGeneratingInProgress" class="generation-progress-banner">
        <div class="progress-content">
          <div class="progress-text">
            <strong
              >Please wait while we generate your data (this may take several
              minutes).</strong
            >
            <span>You may open a new window or navigate elsewhere.</span>
          </div>
        </div>
      </div>

      <div>
        <gc-header-form
          :headerOptions="getHeaderScreenJson"
          :data="loaded ? model : {}"
          :backButtonRoute="backButtonRoute"
          ref="gc-header-form-component"
          :getHeaderToolbar="undefined"
          :setLoading="showLoading"
          :formContentReady="onHeaderFormContentReady"
          :IsHeaderExpanded="IsHeaderExpanded"
        ></gc-header-form>
      </div>

      <div class="gc-details-grid" v-if="baseView.mode !== 'create'">
        <gc-filter-split-view
          :showLeftContainer="showLeftContainer"
          :showRightContainer="showRightContainer"
          :rightContainerWidth="250"
        >
          <template #leftContent>
            <dx-data-grid
              @toolbar-preparing="toolbarPreparing($event)"
              :height="getGridHeight"
              :columns="getColumns"
              :data-source="getDataSource"
              ref="dx-data-grid-comp"
              :key-expr="'ID'"
              :repaint-changes-only="true"
              :show-borders="true"
              :two-way-binding-enabled="false"
              :focusedRowEnabled="false"
              :column-chooser="{ enabled: false }"
              :pager="{ visible: false }"
              :remote-operations="{ groupPaging: true }"
              @cellPrepared="onCellPrepared"
              @content-ready="onContentReady"
            >
              <DxPaging :page-size="50" :enabled="true" />
              <DxScrolling
                :mode="'virtual'"
                :row-rendering-mode="'virtual'"
                :preloadEnabled="true"
              />

              <template #VarietyGroupCellTemplate="{ data }">
                <div class="informer">
                  <span>{{ data.displayValue }}</span>
                </div>
              </template>

              <template #Decimal="{ data }">
                <GcDecimal :dataValue="data" :format="data.column.format" />
              </template>

              <template #Hyperlink="{ data }">
                <span
                  v-if="
                    data.data[data.column.dataField] != 0 &&
                    data.data[data.column.dataField] != ''
                  "
                >
                  <a @click="onHyperlinkClicked(data)">
                    <span v-if="data.column.format">
                      <GcDecimal
                        :dataValue="data"
                        :format="data.column.format"
                      />
                    </span>
                    <span v-else>{{ data.text }}</span>
                  </a>
                </span>
                <span v-else>
                  <span v-if="data.column.format">
                    <GcDecimal :dataValue="data" :format="data.column.format" />
                  </span>
                  <span v-else>{{ data.text }}</span>
                </span>
              </template>

              <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
              <template #masterDetailTemplate="{ data: masterData }">
                <dx-data-grid
                  @toolbar-preparing="childGridToolbarPreparing($event)"
                  :height="'auto'"
                  :columns="getChildGridColumns"
                  :data-source="
                    masterData.data.AlmondcoCropTrackerRunReviewList
                  "
                  ref="dx-data-grid-comp-master-detail"
                  :repaint-changes-only="true"
                  :show-borders="true"
                  :two-way-binding-enabled="false"
                  :focusedRowEnabled="false"
                  :column-chooser="{ enabled: false }"
                  :pager="{ visible: false }"
                  :remote-operations="{ groupPaging: true }"
                  @cellPrepared="onCellPrepared"
                  @content-ready="onContentReady"
                  :loadPanel="{ enabled: true }"
                >
                  <template #Decimal="{ data }">
                    <GcDecimal :dataValue="data" :format="data.column.format" />
                  </template>

                  <template #Hyperlink="{ data }">
                    <span
                      v-if="
                        data.data[data.column.dataField] != 0 &&
                        data.data[data.column.dataField] != ''
                      "
                    >
                      <a @click="onHyperlinkClicked(data)">
                        <span v-if="data.column.format">
                          <GcDecimal
                            :dataValue="data"
                            :format="data.column.format"
                          />
                        </span>
                        <span v-else>{{ data.text }}</span>
                      </a>
                    </span>
                    <span v-else>
                      <span v-if="data.column.format">
                        <GcDecimal
                          :dataValue="data"
                          :format="data.column.format"
                        />
                      </span>
                      <span v-else>{{ data.text }}</span>
                    </span>
                  </template>
                </dx-data-grid>
              </template>
            </dx-data-grid>
          </template>

          <template #rightContent>
            <gc-filter-panel
              :toolbarVisible="true"
              :toolbar="filterToolbar"
              :formItemData="getFilterFormItems"
              :setGridPanelFilter="setGridPanelFilter"
              :gridFoolter="false"
              :showLoading="showLoading"
              :from="filterFrom"
              :isNormal="false"
              :persistenceKey="undefined"
              :key="'fp-call-schedule'"
              :overWriteClear="true"
              :heightAdjustment="heightAdjustment"
            ></gc-filter-panel>
          </template>
        </gc-filter-split-view>

        <GcFooter />
      </div>

      <ColumnDetailsPopup :visible.sync="showColumnDetails" />
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import { mapGetters } from "vuex";
import { ProductBatchService } from "../../../_services/api/inventory/product-batch.service";
import { backwardTabbing } from "../../../_helpers/ui/keyboard";
import { CropTrackerService } from "../../../_services/api/demand-planning/crop-tracker.service";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import ColumnDetailsPopup from "../../../popups/demand-planning/gc-popup-column-info.vue";

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {
    ColumnDetailsPopup,
  },
  data() {
    return {
      loaded: false,
      onFocusIn: false,
      headerForm: null,
      model: {
        IncludeStockOnhand: true,
        IncludeSalesForecast: true,
        IncludeSalesContract: true,
        IncludeAllocationContracts: true,
        IncludeSalesOrders: true,
        IncludePurchaseForecast: true,
        IncludePurchaseOrders: true,
        IncludeProcessRequirements: true,
        IncludeProductionForecast: true,
        IncludeWorkOrders: true,
        IncludeDraftWorkOrders: true,
        BillType: null,
        StartDate: null,
        EndDate: null,
        RetainUntil: null,
        BranchCodes: [],
        Narration: "",
        Clear: null,
        Generate: null,
        AlmondcoIntakeSeason: null,
        AlmondcoIntakeEstimate: null,
        MRPName: "",
      },
      IsHeaderExpanded: true,
      showLeftContainer: true,
      showRightContainer: false,
      headerFormHeight: 0,
      expandedFormHeight: 335,
      IsGeneratingInProgress: false,
      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.closeFilterPanel();
          },
        },
      },
      finalFilter: [],
      filterFrom: "none",
      heightAdjustment: 34,
      IsGroupExpanded: false,
      showColumnDetails: false,
      expandFilter: null,
    };
  },
  async created() {
    // Fetch data if not in create mode
    if (this.baseView.mode !== "create") {
      this.IsHeaderExpanded = false;
      await this.getCropTrackerHeaderDetails();
    }

    /* Set the page key to force re-rendering when needed */
    this.$nextTick(() => {
      this.loaded = true;

      if (this.baseView.mode !== "create") {
        const headerForm = document.querySelectorAll(".gc-header-form");
        setTimeout(() => {
          headerForm[0].style.padding = "unset";
          // this.headerFormHeight = 55; // Collapsed height
          this.$nextTick(() => {
            this.filterFrom = "headerCollapse";

            var dataGrid = this.$refs["dx-data-grid-comp"].instance;
            if (dataGrid) {
              dataGrid.option("height", this.getGridHeight + 10);
              dataGrid.repaint();
            }
          });
        }, 1);
      }
    });
  },
  computed: {
    /**
     * Computed property to get the hotkey save state from Vuex store
     * @returns {boolean} The hotkey save state
     */
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),
    ...mapGetters("notification", ["notificationId", "getNotificationPayload"]),

    /**
     * Computed property to get the back button route
     * @returns {string} The back button route
     */
    backButtonRoute() {
      return "/crop-tracker";
    },

    /**
     * Computed property to get the header screen JSON configuration
     * @returns {Object} The header screen JSON configuration
     */
    getHeaderScreenJson() {
      return {
        headerFormVisible: true,
        headerToolbar: {
          visible: this.baseView.mode !== "create",
          expand: true,
          expandCallback: (e) => {
            this.onExpandCallback(e);
          },
          toolbarFormItems: [
            {
              dataField: "MRPName",
              label: { text: "Name" },
              editorType: "dxTextBox",
              editorOptions: {
                tabIndex: 1,
                width: 300,
                onKeyDown: (e) => {
                  if (backwardTabbing(e)) {
                    e.event.preventDefault();
                  }
                },
                maxLength: 100,
              },
              required: true,
            },
          ],
        },
        formItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 2,
            items: [
              {
                dataField: "MRPName",
                label: { text: "Name" },
                editorType: "dxTextBox",
                editorOptions: {
                  tabIndex: 1,
                  onKeyDown: (e) => {
                    if (backwardTabbing(e)) {
                      e.event.preventDefault();
                    }
                  },
                  maxLength: 100,
                },
                required: true,
              },
            ],
            visible: this.baseView.mode === "create",
          },
          {
            itemType: "group",
            caption: "",
            colCount: 3,
            items: [
              {
                itemType: "group",
                caption: "",
                colCount: 2,
                items: [
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 1,
                    items: [
                      {
                        dataField: "IncludeStockOnhand",
                        label: { text: "Stock Onhand" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 2,
                        },
                      },
                      {
                        dataField: "IncludeSalesForecast",
                        label: { text: "Sales Forecast" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 3,
                        },
                      },
                      {
                        dataField: "IncludeSalesContract",
                        label: { text: "Sales Contract" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 4,
                          onValueChanged: (e) => {
                            if (!e.event) return;
                            this.onSalesContractValueChanged(e);
                          },
                        },
                      },
                      {
                        dataField: "IncludeAllocationContracts",
                        label: { text: "Include Allocation Contract" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 5,
                        },
                      },
                      {
                        dataField: "IncludeSalesOrders",
                        label: { text: "Sales Orders" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 6,
                        },
                      },
                    ],
                  },
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 1,
                    items: [
                      {
                        dataField: "IncludePurchaseForecast",
                        label: { text: "Purchase Forecast" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 7,
                        },
                      },
                      {
                        dataField: "IncludePurchaseOrders",
                        label: { text: "Purchase Orders" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 8,
                        },
                      },
                      {
                        dataField: "IncludeProcessRequirements",
                        label: { text: "Process Requirements" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 9,
                          onValueChanged: (e) => {
                            if (!e.event) return;
                            this.clearBillTypeValue(e);
                          },
                        },
                      },

                      {
                        dataField: "IncludeProductionForecast",
                        label: { text: "Production Forecast" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 10,
                          onValueChanged: (e) => {
                            if (!e.event) return;
                            this.clearBillTypeValue(e);
                          },
                        },
                      },
                    ],
                  },
                ],
              },
              {
                itemType: "group",
                caption: "",
                colSpan: 2,
                colCount: 2,
                items: [
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 1,
                    items: [
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 2,
                    items: [
                      {
                        dataField: "IncludeWorkOrders",
                        label: { text: "Work Orders" },
                        editorType: "dxSwitch",
                        editorOptions: {
                          tabIndex: 11,
                          onValueChanged: (e) => {
                            if (!e.event) return;
                            this.onWorkOrdersValueChange(e);
                          },
                        },
                      },
                      {
                        dataField: "IncludeDraftWorkOrders",
                        label: { text: "Include Draft Work Orders" },
                        editorType: "dxSwitch",
                        cssClass: "include-draft-work-orders",
                        editorOptions: {
                          tabIndex: 12,
                        },
                      },
                    ],
                  },
                  {
                    screenField: "BillType",
                    dataField: "BillType",
                    label: { text: "Bill Type" },
                    editorOptions: {
                      tabIndex: 13,
                      showClearButton: true,
                    },
                    dataSourceConfig: {
                      endpoint: "api/BillType",
                      sort: {
                        selector: "Description",
                        asc: true,
                      },
                      filter:
                        this.baseView.mode === "create"
                          ? [
                              ["Inactive", "=", "N"],
                              "and",
                              ["Code", "<>", "Maintenance"],
                            ]
                          : [["Inactive", "=", "N"]],
                    },
                    valueExpr: "Code",
                    displayExpr: "Description",
                    validationRules: [
                      {
                        type: "custom",
                        validationCallback: ({ value, rule }) => {
                          rule.message = `Bill Type is required.`;
                          if (
                            this.model.IncludeProductionForecast &&
                            this.model.IncludeProcessRequirements
                          ) {
                            return !!value; // must have a value
                          }
                          return true; // otherwise, always valid
                        },
                        reevaluate: true,
                      },
                    ],
                  },
                  {
                    screenField: "Branch",
                    dataField: "BranchCodes",
                    label: { text: "Branch" },
                    editorType: "dxTagBox",
                    multiSelect: true,
                    editorOptions: {
                      tabIndex: 14,
                    },
                  },
                ],
                  },
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 1,
                    items: [
                  {
                    dataField: "AlmondcoIntakeSeason",
                    screenField: "AlmondcoIntakeSeason",
                    filter: [["EndDate", ">=", new Date()]],
                    searchExpr: ["SeasonAsString"],
                    editorOptions: {
                      tabIndex: 15,
                      onValueChanged: (e) => {
                        if (!e.event) return;
                        this.onAlmondcoIntakeSeasonChange(e);
                      },
                      showClearButton: true,
                      searchMode: "startswith",
                    },
                    required: true,
                  },
                  {
                    dataField: "AlmondcoIntakeEstimate",
                    editorType: "dxSelectBox",
                    label: { text: "Estimate" },
                    editorOptions: {
                      dataSource: ProductBatchService.dataSourceConfig(
                        "api/AlmondcoIntakeEstimateList",
                        {
                          filter: [
                            ["Season", "=", this.model.AlmondcoIntakeSeason],
                          ],
                          fieldType: {
                            RecordId: "Guid",
                          },
                          keyType: {
                            RecordId: "Guid",
                          },
                        }
                      ),
                      displayExpr: "Description",
                      valueExpr: "RecordId",
                      searchExpr: ["Description"],
                      searchMode: "contains",
                      showClearButton: true,
                      searchEnabled: true,
                      readOnly: !this.model.AlmondcoIntakeSeason,
                      tabIndex: 16,
                      onInitialized: (e) => {
                        if (!this.model.AlmondcoIntakeSeason) {
                          e.component.option("readOnly", true);
                          e.component.option("tabIndex", -1);
                        } else {
                          e.component.option("tabIndex", 16);
                        }
                      },
                    },
                  },
                  {
                    dataField: "RetainUntil",
                    label: { text: "Retain Until Date" },
                    editorType: "dxDateBox",
                    editorOptions: {
                      displayFormat: "dd MMM yyyy",
                      tabIndex: 17,
                      min: new Date(),
                      invalidDateMessage: null,
                    },
                    validationRules: [
                      {
                        type: "custom",
                        validationCallback: function (e) {
                          // allow empty
                          if (!e.value) return true;

                          const today = new Date();
                          today.setHours(0, 0, 0, 0); // Normalize time
                          const selectedDate = new Date(e.value);
                          selectedDate.setHours(0, 0, 0, 0);
                          return selectedDate >= today;
                        },
                        message: "Start Date cannot be in the past",
                      },
                    ],
                  },
                ],
                  },
                  {
                    dataField: "Narration",
                    colSpan: 2,
                    label: { text: "Narration" },
                    labelLocation: "top",
                    editorType: "dxTextArea",
                    editorOptions: {
                      height: 90,
                      placeholder: "Enter narration here",
                      tabIndex: 20,
                      maxLength: 1000,
                    },
                  },
                ],
              },
            ],
          },

          {
            itemType: "group",
            caption: "",
            colCount: 1,
            cssClass: "generate-button-group",
            visible: this.baseView.mode !== "review",
            items: [
              {
                dataField: "Clear",
                label: {
                  text: " ",
                  visible: false,
                },
                editorType: "dxButton",
                cssClass: "btn-clear",
                editorOptions: {
                  validationGroup: "gc-header-form",
                  onClick: async () => {
                    this.onClearClick();
                  },
                  text: "Clear",
                  type: "normal",
                  rtlEnabled: false,
                  tabIndex: 18,
                },
                disabled: this.IsGeneratingInProgress,
              },
              {
                dataField: "Generate",
                label: {
                  text: " ",
                  visible: false,
                },
                editorType: "dxButton",
                cssClass: "btn-generate",
                editorOptions: {
                  validationGroup: "gc-header-form",
                  onClick: async () => {
                    this.onGenerateClick();
                  },
                  text: this.IsGeneratingInProgress
                    ? "Generating..."
                    : "Generate",
                  type: "default",
                  rtlEnabled: false,
                  tabIndex: 19,
                },
                disabled: this.IsGeneratingInProgress,
              },
            ],
          },
        ],
      };
    },

    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),

    /**
     * Computed property to calculate the height of the data grid
     * @returns {number} The height of the data grid
     */
    getGridHeight() {
      return this.gridHeight - this.headerFormHeight - 20 - 40;
      // 20 => Top & Bottom Padding
    },

    /**
     * Computed property to get the columns configuration for the data grid
     * @returns {Array} The columns configuration
     */
    getColumns() {
      return [
        {
          screenField: "Variety",
          caption: "Variety",
          dataField: "VarietyCode",
          sortIndex: 1,
          sortOrder: "asc",
          allowHiding: false,
          groupCellTemplate: "VarietyGroupCellTemplate",
          hyperLinkDataField: "VarietyCode",
          showInColumnChooser: false,
          minWidth: 100,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${info.data.VarietyName}
                        </span>
                      <span class="record-secondary gc-grid-code">
                        ${info.data.VarietyCode}
                      </span>
                    </div>`);
          },
        },
        {
          caption: "Completed",
          dataField: "Completed",
          width: 80,
          alignment: "center",
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class="custom-complete">
                        <span class="primary-complete">
                          ${info.data.Completed}
                        </span>
                    </div>`);
          },
        },
        {
          caption: "Estimate",
          dataField: "OriginalEstimate",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "Received",
          dataField: "Received",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "Remaining",
          dataField: "RemainingEstimate",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "Unreleased",
          dataField: "UnreleasedStock",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "Carry In",
          dataField: "CarryIn",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Hyperlink",

          hyperlink: {
            url: "crop-tracker-carry-in-detail",
            target: "_blank",
            params: ["Variety", "MRPRun", "AlmondcoBulkStockCode"],
          },
        },
        {
          caption: "On Hand",
          dataField: "OnHand",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Hyperlink",
          hyperlink: {
            url: "crop-tracker-on-hand-detail",
            target: "_blank",
            params: ["Variety", "MRPRun", "AlmondcoBulkStockCode"],
          },
        },
        {
          caption: "Total",
          dataField: "TotalBulkStock",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "O/S Sales Orders",
          dataField: "OutstandingSalesOrders",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Hyperlink",
          hyperlink: {
            url: "mrp-outstanding-sales-order-detail",
            target: "_blank",
            params: [
              "Variety",
              "MRPRun",
              "FiscalYear",
              "AlmondcoBulkStockCode",
            ],
          },
        },
        {
          screenField: "FiscalYear",
          visible: false,
          showInColumnChooser: false,
          hyperLinkDataField: "SeasonYear",
        },
        {
          caption: "Undrawn Contracts",
          dataField: "UndrawnContracts",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Hyperlink",
          hyperlink: {
            url: "mrp-contract-detail",
            target: "_blank",
            params: [
              "Variety",
              "MRPRun",
              "FiscalYear",
              "AlmondcoBulkStockCode",
            ],
          },
        },
        {
          caption: " O/S Sales Forecast",
          dataField: "OutstandingSalesForecast",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Hyperlink",
          hyperlink: {
            url: "mrp-outstanding-sales-forcast",
            target: "_blank",
            params: [
              "Variety",
              "MRPRun",
              "FiscalYear",
              "AlmondcoBulkStockCode",
            ],
          },
        },
        // {
        //   caption: "Make to Stock",
        // },
        {
          caption: "Allocated",
          dataField: "AllocatedVolume",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "",
          dataField: "AllocationVolumePct",
          format: "##0.00 '%';(##0.00 '%')",
          width: 80,
          // minWidth: ColumnWidth.Percentage.minWidth,
          cellTemplate: "Decimal",
          summaryType: "avg",
          allowSorting: false,
        },
        {
          caption: "Unallocated",
          dataField: "UnallocatedVolume",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "",
          dataField: "UnallocatedVolumePct",
          format: "##0.00 '%';(##0.00 '%')",
          width: 80,
          // minWidth: ColumnWidth.Percentage.minWidth,
          cellTemplate: "Decimal",
          summaryType: "avg",
          allowSorting: false,
        },
        {
          caption: "Reclaim",
          dataField: "ReclaimPct",
          format: "##0.00 '%'",
          width: 80,
          // minWidth: ColumnWidth.Percentage.minWidth,
          cellTemplate: "Decimal",
        },
        {
          caption: "Saleable",
          dataField: "SaleableVolume",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
      ];
    },

    /**
     * Computed property to get the columns configuration for the data grid
     * @returns {Array} The columns configuration
     */
    getChildGridColumns() {
      return [
        {
          caption: "Size",
          dataField: "SizeCode",
          sortIndex: 1,
          sortOrder: "asc",
          visible: false,
          showInColumnChooser: false,
        },
        {
          caption: "Bulk Stock Code",
          dataField: "ProductDescription",
          minWidth: 150,
          // width: ColumnWidth.ProductDescription.width,
          allowHiding: false,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${info.data.ProductDescription}
                        </span>
                      <span class="record-secondary gc-grid-code">
                        ${info.data.BulkStockCode}
                      </span>
                    </div>`);
          },
        },
        {
          caption: "Estimate",
          dataField: "OriginalEstimate",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "Received",
          dataField: "Received",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "Remaining",
          dataField: "RemainingEstimate",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "Unreleased",
          dataField: "UnreleasedStock",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "Carry In",
          dataField: "CarryIn",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Hyperlink",
          hyperlink: {
            url: "crop-tracker-carry-in-detail",
            target: "_blank",
            params: ["Variety", "MRPRun", "AlmondcoBulkStockCode"],
          },
        },
        {
          caption: "On Hand",
          dataField: "OnHand",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Hyperlink",
          hyperlink: {
            url: "crop-tracker-on-hand-detail",
            target: "_blank",
            params: ["Variety", "MRPRun", "AlmondcoBulkStockCode"],
          },
        },
        {
          caption: "Total",
          dataField: "TotalBulkStock",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "O/S Sales Orders",
          dataField: "OutstandingSalesOrders",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Hyperlink",
          hyperlink: {
            url: "mrp-outstanding-sales-order-detail",
            target: "_blank",
            params: [
              "Variety",
              "MRPRun",
              "FiscalYear",
              "AlmondcoBulkStockCode",
            ],
          },
        },
        {
          screenField: "FiscalYear",
          visible: false,
          showInColumnChooser: false,
          hyperLinkDataField: "SeasonYear",
        },
        {
          caption: "Undrawn Contracts",
          dataField: "UndrawnContracts",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Hyperlink",
          hyperlink: {
            url: "mrp-contract-detail",
            target: "_blank",
            params: [
              "Variety",
              "MRPRun",
              "FiscalYear",
              "AlmondcoBulkStockCode",
            ],
          },
        },
        {
          caption: " O/S Sales Forecast",
          dataField: "OutstandingSalesForecast",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Hyperlink",
          hyperlink: {
            url: "mrp-outstanding-sales-forcast",
            target: "_blank",
            params: [
              "Variety",
              "MRPRun",
              "FiscalYear",
              "AlmondcoBulkStockCode",
            ],
          },
        },
        // {
        //   caption: "Make to Stock",
        // },
        {
          caption: "Allocated",
          dataField: "AllocatedVolume",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "",
          dataField: "AllocationVolumePct",
          format: "##0.00 '%';(##0.00 '%')",
          width: 80,
          // minWidth: ColumnWidth.Percentage.minWidth,
          cellTemplate: "Decimal",
          summaryType: "avg",
          allowSorting: false,
        },
        {
          caption: "Unallocated",
          dataField: "UnallocatedVolume",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
        {
          caption: "",
          dataField: "UnallocatedVolumePct",
          format: "##0.00 '%';(##0.00 '%')",
          width: 80,
          // minWidth: ColumnWidth.Percentage.minWidth,
          cellTemplate: "Decimal",
          summaryType: "avg",
          allowSorting: false,
        },
        {
          caption: "",
          dataField: "",
          format: "##0.00 '%'",
          width: 80,
          // minWidth: ColumnWidth.Percentage.minWidth,
          cellTemplate: "Decimal",
          allowSorting: false,
        },
        {
          caption: "Saleable",
          dataField: "SaleableVolume",
          format: "##,###,###,###,##0;(##,###,###,###,##0)",
          summaryType: "sum",
          width: ColumnWidth.Quantity4DecimalPoints.width - 12,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth - 12,
          cellTemplate: "Decimal",
        },
      ];
    },

    /**
     * Computed property to get the data source for the data grid
     * @returns {Object} The data source
     */
    getDataSource() {
      return this.gridRemoteDataSource;
    },

    /**
     * Computed property to create the remote data source for the data grid
     * @returns {Object} The remote data source
     */
    gridRemoteDataSource() {
      return CropTrackerService.dataSourceConfig(
        "api/AlmondcoCropTrackerRunReviewGroupByVarietyCode",
        {
          filter: this.getFinalFilter,
          key: [
            "MRPRun",
            "SeasonYear",
            "VarietyCode",
            "SizeCode",
            "BulkStockCode",
          ],
          expand: this.getExpand,
        }
      );
    },

    /**
     * Computed property to get the expand string for the data grid
     * @returns {string} The expand string
     */
    getExpand() {
      if (this.expandFilter && this.expandFilter.length > 0) {
        return `AlmondcoCropTrackerRunReviewList($filter=${this.expandFilter})`;
      } else {
        return "AlmondcoCropTrackerRunReviewList";
      }
    },

    /**
     * Computed property to get the default filter for the data grid
     * @returns {Array} The default filter
     */
    defaultFilter() {
      return ["MRPRun", "=", parseInt(this.baseView.id)];
    },

    /**
     * Computed property to get the final filter for the data grid
     * @returns {Array} The final filter
     */
    getFinalFilter() {
      return this.finalFilter.length === 0
        ? this.defaultFilter
        : this.finalFilter;
    },

    /**
     * Computed property to get the filter form items for the filter panel
     * @returns {Array} The filter form items
     */
    getFilterFormItems() {
      return [
        {
          screenField: "ProductCategory3",
          filterLabel: "Variety",
          filterDataField: "VarietyCode",
          filterIndex: 5,
          searchExpr: ["Code", "Description"],
        },
        {
          screenField: "ProductSize",
          label: "Size",
          filterLabel: "Size",
          // filterDataField: "AlmondcoCropTrackerRunReviewList.SizeCode",
          filterIndex: 8,
          filterDataRelationship: "Child",
          filterOperator: "=",
          filterDataField: "AlmondcoCropTrackerRunReviewList/SizeCode",
        },
      ];
    },
  },
  methods: {
    /**
     * Function to handle the header form expand/collapse event
     * @param {boolean} e - The expand/collapse state
     * @returns {void}
     */
    onExpandCallback(e) {
      const headerForm = document.querySelectorAll(".gc-header-form");
      if (!e) {
        setTimeout(() => {
          headerForm[0].style.padding = "unset";
          this.headerFormHeight = 55; // Collapsed height
          this.$nextTick(() => {
            this.filterFrom = e ? "headerExpand" : "headerCollapse";
          });
        }, 350);
      } else {
        headerForm[0].style.padding = "0 0 10px 0";
        this.headerFormHeight = this.expandedFormHeight;
        this.$nextTick(() => {
          this.filterFrom = e ? "headerExpand" : "headerCollapse";
        });
      }

      this.$nextTick(() => {
        this.IsHeaderExpanded = e;
        this.$nextTick(() => {
          this.reSizeGrid();
        });
      });
    },
    /* Function to handle the Almondco Intake Season change event */
    onAlmondcoIntakeSeasonChange(e) {
      this.model.AlmondcoIntakeSeason = e.value;
      this.updateEstimateField(e);
    },

    // Initialize the form data
    initData() {
      this.model = {
        MRPName: "",
        IncludeStockOnhand: true,
        IncludeSalesForecast: true,
        IncludeSalesContract: true,
        IncludeAllocationContracts: true,
        IncludeSalesOrders: true,
        IncludePurchaseForecast: true,
        IncludePurchaseOrders: true,
        IncludeProcessRequirements: true,
        IncludeProductionForecast: true,
        IncludeWorkOrders: true,
        IncludeDraftWorkOrders: true,
        BillType: null,
        StartDate: null,
        EndDate: null,
        RetainUntil: new Date().setMonth(new Date().getMonth() + 3),
        BranchCodes: [],
        Narration: "",
        Clear: null,
        Generate: null,
        // AlmondcoIntakeSeason: null,
        // AlmondcoIntakeEstimate: null,
      };

      // Set default BillType value
      const billTypeEditor = this.headerForm.getEditor("BillType");
      if (billTypeEditor && !this.model.BillType) {
        const dataSource = billTypeEditor.getDataSource();
        dataSource.load().then((items) => {
          if (items && items.length > 0) {
            this.model.BillType = "Production";
          }
        });
      }
    },

    /**
     * Function to navigate back to the MRP list page
     * @returns {void}
     */
    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },

    /**
     * Function to handle the form content ready event
     * @param {Object} e - The event object
     * @returns {void}
     */
    onHeaderFormContentReady(e) {
      if (e && this.baseView.mode != "review" && !this.onFocusIn) {
        this.onFocusIn = true;
        this.$nextTick(() => {
          setTimeout(() => {
            this.headerForm = e.component;
            this.initData();
            this.focusOnHeaderForm();
            this.onWorkOrdersValueChange();
            this.onSalesContractValueChanged({ value: this.model.IncludeSalesContract });
            this.updateEstimateField({ value: false });
          }, 500);
        });
      }

      const headerForm = document.querySelectorAll(".gc-header-form");
      this.headerFormHeight = headerForm[0].clientHeight;
      // this.expandedFormHeight = headerForm[0].clientHeight;
    },

    /**
     * Function to focus on the Stock On Hand field in the header form
     * @returns {void}
     */
    async focusOnHeaderForm(dataField = "MRPName") {
      setTimeout(() => {
        this.$nextTick(() => {
          if (this.headerForm && this.headerForm.getEditor(dataField)) {
            let branchEditor = this.headerForm.getEditor(dataField).instance();
            if (branchEditor) {
              branchEditor.focus();
            }
          }
        });
      }, 100);
    },

    /**
     * Function to handle the value change event of the Work Orders field
     * Disables or enables the Include Draft Work Orders field based on the value of Work Orders
     * @returns {void}
     */
    onWorkOrdersValueChange() {
      const draftWorkOrdersEditor = this.headerForm.getEditor(
        "IncludeDraftWorkOrders"
      );
      if (draftWorkOrdersEditor) {
        if (!this.model.IncludeWorkOrders) {
          draftWorkOrdersEditor.option("disabled", true);
          draftWorkOrdersEditor.option("tabIndex", -1);
          draftWorkOrdersEditor.option("value", false);
        } else {
          draftWorkOrdersEditor.option("disabled", false);
          draftWorkOrdersEditor.option("tabIndex", 12);
        }
      }
    },

    onSalesContractValueChanged(e) {
      const allocationEditor = this.headerForm.getEditor(
        "IncludeAllocationContracts"
      );
      if (allocationEditor) {
        if (!e.value) {
          allocationEditor.option("disabled", true);
          allocationEditor.option("tabIndex", -1);
          allocationEditor.option("value", false);
          this.model.IncludeAllocationContracts = false;
        } else {
          allocationEditor.option("disabled", false);
          allocationEditor.option("tabIndex", 5);
        }
      }
    },

    /**
     * Function to clear the Bill Type value based on the values of Include Production Forecast and Include Process Requirements
     * @returns {void}
     */
    clearBillTypeValue() {
      const billTypeEditor = this.headerForm.getEditor("BillType");
      if (
        !this.model.IncludeProductionForecast &&
        !this.model.IncludeProcessRequirements
      ) {
        // this.model.BillType = null;
        if (billTypeEditor) {
          billTypeEditor.option("value", null);
        }
      }

      if (
        !this.model.IncludeProductionForecast ||
        !this.model.IncludeProcessRequirements
      ) {
        billTypeEditor.option("readOnly", true);
        billTypeEditor.option("tabIndex", -1);
      } else {
        billTypeEditor.option("readOnly", false);
        billTypeEditor.option("tabIndex", 12);
        billTypeEditor.option("value", this.model.BillType);
      }

      this.validateForm();
    },

    /**
     * Function to handle the Generate button click event
     * Validates the form and saves the MRP header if the form is valid
     * @returns {void}
     */
    async onGenerateClick() {
      const form =
        this.$refs["gc-header-form-component"].$refs["gc-header-form"];
      if (form) {
        const validations = await form.getFormValidations();

        // Check if at least one checkbox is selected
        const atLeastOneCheckboxSelected =
          this.model.IncludeStockOnhand ||
          this.model.IncludeSalesForecast ||
          this.model.IncludeSalesContract ||
          this.model.IncludeSalesOrders ||
          this.model.IncludePurchaseForecast ||
          this.model.IncludePurchaseOrders ||
          this.model.IncludeProcessRequirements ||
          this.model.IncludeProductionForecast ||
          this.model.IncludeWorkOrders;

        if (!validations.isValid) {
          this.displayValidationSummary(validations.brokenRules);
        } else if (!atLeastOneCheckboxSelected) {
          validations.brokenRules.push({
            message:
              "Crop Tracker run cannot be executed if all toggles are off",
          });
          this.displayValidationSummary(validations.brokenRules);
        } else {
          this.saveCropTracker();
        }
      }
    },

    /**
     * Function to save the MRP header
     * Displays a success notification and navigates back to the MRP list page upon successful save
     * @returns {void}
     */
    async saveCropTracker() {
      this.showLoading(true);
      this.IsGeneratingInProgress = true;
      try {
        await CropTrackerService.save(this.getPayload());
      } catch (error) {
        this.displayServerError(error);
      }
    },

    /**
     * Function to construct the payload for saving the MRP header
     * @returns {Object} The payload object
     */
    getPayload() {
      const payload = {
        MRPName: this.model.MRPName,
        BillType: this.model.BillType,
        BranchCodes: this.model.BranchCodes.join(","),
        StartDate: null,
        EndDate: null,
        RetainUntil: this.model.RetainUntil
          ? new Date(this.model.RetainUntil).toSaveDate()
          : null,
        IncludeDraftWorkOrders: this.model.IncludeDraftWorkOrders,
        IncludeProcessRequirements: this.model.IncludeProcessRequirements,
        IncludeProductionForecast: this.model.IncludeProductionForecast,
        IncludePurchaseForecast: this.model.IncludePurchaseForecast,
        IncludePurchaseOrders: this.model.IncludePurchaseOrders,
        IncludeAllocationContracts: this.model.IncludeAllocationContracts,
        IncludeSalesContract: this.model.IncludeSalesContract,
        IncludeSalesForecast: this.model.IncludeSalesForecast,
        IncludeSalesOrders: this.model.IncludeSalesOrders,
        IncludeStockOnhand: this.model.IncludeStockOnhand,
        IncludeWorkOrders: this.model.IncludeWorkOrders,
        Narration: this.model.Narration,
        SeasonYear: this.model.AlmondcoIntakeSeason,
        IntakeEstimateRecordId: this.model.AlmondcoIntakeEstimate,
      };

      return payload;
    },

    /**
     * Function to handle the Clear button click event
     * Resets the form values and focuses on the Stock On Hand field
     * @returns {void}
     */
    onClearClick() {
      const form = this.HeaderFormInstance();
      if (form) {
        // form.resetValues();
        this.initData();

        this.$nextTick(() => {
          this.focusOnHeaderForm();
          this.onWorkOrdersValueChange();
          this.onSalesContractValueChanged({ value: this.model.IncludeSalesContract });
        });
      }
    },

    /**
     * Function to get the instance of the header form
     * @returns {Object} The header form instance
     */
    HeaderFormInstance() {
      if (this.loaded)
        return this.$refs["gc-header-form-component"].$refs["gc-header-form"]
          .$refs["gc-form-comp"].instance;
    },

    /**
     * Function to update the Almondco Intake Estimate field based on the selected Almondco Intake Season
     * @param {Object} e - The event object containing the selected value
     * @returns {void}
     */
    updateEstimateField(e) {
      const estimateEditor = this.headerForm.getEditor(
        "AlmondcoIntakeEstimate"
      );

      if (estimateEditor) {
        if (e.value) {
          estimateEditor.option("readOnly", false);
          // Replace the dataSource with the updated filter
          estimateEditor.option(
            "dataSource",
            ProductBatchService.dataSourceConfig(
              "api/AlmondcoIntakeEstimateList",
              {
                filter: [["Season", "=", e.value]],
              }
            )
          );

          // Optional: reset value
          estimateEditor.option("value", null);
          estimateEditor.option("tabIndex", 16);
        } else {
          estimateEditor.option("readOnly", true);
          estimateEditor.option("value", null);
          estimateEditor.option("dataSource", []); // clear list
          estimateEditor.option("tabIndex", -1);
        }
      }
    },

    /**
     * Function to validate the header form
     * @returns {void}
     */
    validateForm() {
      const form = this.HeaderFormInstance();
      if (form) {
        form.validate();
      }
    },

    /**
     * Function to get the Crop Tracker header details for review mode
     * Populates the model with the retrieved data
     * @returns {void}
     */
    async getCropTrackerHeaderDetails() {
      this.showLoading(true);
      try {
        const response = await CropTrackerService.getCropTrackerHeader(
          this.baseView.id
        );
        if (
          response &&
          response.data &&
          response.data.value &&
          response.data.value.length > 0
        ) {
          this.model = Object.assign(this.model, {
            ...response.data.value[0],
          });

          // Parse BranchCodes
          if (this.model.BranchCodes) {
            this.model.BranchCodes = this.model.BranchCodes.split(",");
          } else {
            this.model.BranchCodes = [];
          }

          // Parse dates
          if (this.model.StartDate) {
            this.model.StartDate = new Date(
              this.model.StartDate.replace("Z", "")
            );
          }

          if (this.model.EndDate) {
            this.model.EndDate = new Date(this.model.EndDate.replace("Z", ""));
          }

          if (this.model.RetainUntil) {
            this.model.RetainUntil = new Date(
              this.model.RetainUntil.replace("Z", "")
            );
          }

          // Set AlmondcoIntakeSeason from SeasonYear
          if (this.model.SeasonYear) {
            this.model.AlmondcoIntakeSeason = this.model.SeasonYear;
          }

          // Set AlmondcoIntakeEstimate from IntakeEstimateRecordId
          if (this.model.IntakeEstimateRecordId) {
            this.model.AlmondcoIntakeEstimate =
              this.model.IntakeEstimateRecordId;
          }
        }
      } catch (error) {
        this.displayServerError(error);
      } finally {
        this.showLoading(false);
      }
    },

    // Details grid methods

    /**
     * Function to customize the toolbar of the data grid
     * Adds a filter button to toggle the visibility of the filter panel
     * @param {Object} e - The event object
     * @returns {void}
     */
    toolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });

      // var columnChooserButton = toolbarItems.find((item) => {
      //   return item.name === "columnChooserButton";
      // });

      let itemsToRemoveFromToolBar = ["searchPanel", "columnChooserButton"];

      e.toolbarOptions.items = toolbarItems.filter(
        (item) => !itemsToRemoveFromToolBar.includes(item.name)
      );

      searchPanel.location = "after";
      e.toolbarOptions.items.unshift(
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            text: this.IsGroupExpanded ? "Collapse" : "Expand",
            icon: this.IsGroupExpanded ? "chevrondown" : "chevronright",
            onClick: () => {
              var dataGrid = this.$refs["dx-data-grid-comp"].instance;
              if (this.IsGroupExpanded) {
                dataGrid.collapseAll(-1);
              } else {
                dataGrid.expandAll(-1);
              }
              this.IsGroupExpanded = !this.IsGroupExpanded;
              this.$nextTick(() => {
                dataGrid.repaint();
              });
            },
          },
        },
        // columnChooserButton,

        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            tabIndex: -1,
            validationGroup: "formsData",
            icon: "info",
            onClick: () => {
              this.showColumnDetails = true;
            },
            visible: this.baseView.mode !== "create",
          },
        },
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            tabIndex: -1,
            validationGroup: "formsData",
            icon: "filter",
            elementAttr: { id: "filter-btn" },
            onClick: () => {
              this.showRightContainer = !this.showRightContainer;
              var item = document.getElementById("filter-btn");

              this.$nextTick(() => {
                item.classList.add("dx-state-invisible");
                setTimeout(() => {
                  // this.reSizeGrid();

                  let filter = document.getElementById("filter-btn");
                  filter.classList.add("dx-state-invisible");
                }, 100);
              });
            },
            visible: !this.showRightContainer,
          },
        }
      );
    },

    /**
     * Function to customize the toolbar of the child data grid
     * Hides the toolbar of the child grid
     * @param {Object} e - The event object
     * @returns {void}
     */
    childGridToolbarPreparing(e) {
      e.toolbarOptions.visible = false;
    },

    /**
     * Function to customize the appearance of cells in the data grid
     * Modifies the display of summary items based on their values
     * @param {Object} e - The event object
     * @returns {void}
     */
    onCellPrepared(e) {
      const subTotals = this.getColumns.filter((col) => col.summaryType);
      if (e.rowType === "group") {
        subTotals.forEach((element) => {
          let si = e.summaryItems.find(
            (i) => i.showInColumn == element["dataField"]
          );

          if (si) {
            let cell = e.cellElement.querySelector(".dx-datagrid-summary-item");
            if (si.value == 0) {
              if (cell) cell.innerHTML = "-";
            } else if (si.value < 0 && cell) cell.classList.add("avail-error");
          }
        });
      }
    },

    /**
     * Function to handle the content ready event of the data grid
     * Adds a custom "no results" message if there are no records in the grid
     * @param {Object} e - The event object
     * @returns {void}
     */
    onContentReady(e) {
      if (e) {
        this.$nextTick(() => {
          this.addGridNoResults(e, {
            addNew: {
              class: "no-data",
              title: "No Crop Tracker runs found.",
              message: "There are no Crop Tracker runs linked to this record.",
            },
          });
        });
      }
    },

    /**
     * Function to resize the data grid
     * Updates the dimensions of the data grid instance
     * @returns {void}
     */
    reSizeGrid() {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      dataGrid.beginUpdate();
      dataGrid.updateDimensions();
      dataGrid.endUpdate();
    },

    /**
     * Function to handle hyperlink clicks in the data grid
     * Calls the onCellHyperLinkClick method with the clicked data
     * @param {Object} data - The data object containing hyperlink information
     * @returns {void}
     */
    onHyperlinkClicked(data) {
      this.onCellHyperLinkClick(data);
    },

    // Filter Panel Methods

    /**
     * Function to close the filter panel
     * Toggles the visibility of the filter panel and updates the grid size
     * @returns {void}
     */
    closeFilterPanel() {
      this.showRightContainer = !this.showRightContainer;

      var item = document.getElementById("filter-btn");
      item.classList.remove("dx-state-invisible");

      this.$nextTick(() => {
        this.reSizeGrid();
      });
    },

    /**
     * Function to set the filter for the data grid based on user input
     * @param {Array} e - The filter criteria
     * @param {boolean} valueChanged - Indicates if the filter value has changed
     * @returns {void}
     */
    setGridPanelFilter(e, valueChanged) {
      const expandFilter = this.filterArrayToReadable(e);
      if (expandFilter.length > 0) {
        this.expandFilter = expandFilter;
      } else {
        this.expandFilter = null;
      }

      this.$nextTick(() => {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        // valueChanged === true means filter is applied.
        if (!valueChanged) return;
        else {
          if (e.length > 0) {
            let filter = [];
            filter.push([...this.defaultFilter]);
            filter.push("and");
            filter.push([...e]);

            this.finalFilter = filter;
          } else {
            this.finalFilter = [...this.defaultFilter];
          }
          dataGrid.option("dataSource", this.gridRemoteDataSource);
        }
      });
    },

    /**
     * Function to convert a filter array into a readable string format
     * @param {Array} filter - The filter array
     * @returns {string} The readable filter string
     */
    filterArrayToReadable(filter) {
      if (!Array.isArray(filter)) return "";

      // Single-element expression: ["AlmondcoCropTrackerRunReviewList/any(i: i/SizeCode eq '16/18')"]
      if (filter.length === 1 && typeof filter[0] === "string") {
        const expr = filter[0];
        if (expr.includes("AlmondcoCropTrackerRunReviewList")) {
          const match = expr.match(/any\(i:\s*i\/([^ ]+)\s*eq\s*'([^']+)'\)/);
          if (match) {
            const [, field, val] = match;
            return `${field} eq '${val}'`;
          }
        }
        return ""; // skip non-Almondco expressions
      }

      // Standard condition: [field, op, value]
      if (
        filter.length === 3 &&
        typeof filter[0] === "string" &&
        typeof filter[1] === "string"
      ) {
        const [fieldExpr] = filter;
        if (fieldExpr.includes("AlmondcoCropTrackerRunReviewList")) {
          const match = fieldExpr.match(
            /any\(i:\s*i\/([^ ]+)\s*eq\s*'([^']+)'\)/
          );
          if (match) {
            const [, field, val] = match;
            return `${field} eq '${val}'`;
          }
        }
        return ""; // skip non-Almondco expressions
      }

      // Handle combined logical groups
      let result = [];
      for (let i = 0; i < filter.length; i++) {
        const item = filter[i];
        if (
          typeof item === "string" &&
          (item.toLowerCase() === "or" || item.toLowerCase() === "and")
        ) {
          // only keep operator if there is a valid previous and next condition
          if (
            result.length > 0 &&
            i + 1 < filter.length &&
            this.filterArrayToReadable(filter[i + 1])
          ) {
            result.push(item);
          }
        } else {
          const extracted = this.filterArrayToReadable(item);
          if (extracted) result.push(extracted);
        }
      }

      if (result.length === 0) return "";
      return result.length === 1
        ? result[0]
        : result
            .map((p) => (p === "or" || p === "and" ? p : `(${p})`))
            .join(" ");
    },

    /**
     * Function to get the data source for the child grid
     * @param {Object} data - The data object containing MRPRun and VarietyCode
     * @returns {Object} The data source for the child grid
     */
    getChildDataSource(data) {
      const filter = [
        ["MRPRun", "=", data.MRPRun],
        "and",
        ["VarietyCode", "=", data.VarietyCode],
      ];

      return CropTrackerService.dataSourceConfig(
        "api/AlmondcoCropTrackerRunReview",
        {
          filter: filter,
          key: [
            "MRPRun",
            "SeasonYear",
            "VarietyCode",
            "SizeCode",
            "BulkStockCode",
          ],
        }
      );
    },
  },
  watch: {
    /**
     * Watcher for the getHotKeySave computed property
     * Triggers the onGenerateClick method when getHotKeySave is true
     * @param {boolean} value - The new value of getHotKeySave
     * @returns {void}
     */
    getHotKeySave(value) {
      if (value) {
        this.$store.dispatch("hotKey/setHotKeySave", false);
        this.onGenerateClick();
      }
    },
    notificationId() {
      if (
        this.getNotificationPayload &&
        this.getNotificationPayload.messageType ===
          "AlmondcoCropTrackerRunCompleted"
      ) {
        this.IsGeneratingInProgress = false;
        this.showLoading(false);
        this.setIgnoreRouteChangeConfirmation();
        this.$nextTick(() => {
          this.$router.push({
            path: "/crop-tracker/review/" + this.getNotificationPayload.message,
          });
        });
      }
    },
  },
};
</script>

<style lang="scss">
.crop-tracker-layout {
  .generation-progress-banner {
    background: #ffffff;
    background-color: #ff000021 !important;
    padding: 16px 20px;
    margin-bottom: 10px;

    .progress-content {
      display: flex;
      align-items: center;
      gap: 16px;

      .progress-text {
        display: flex;
        flex-direction: column;
        gap: 4px;

        strong {
          font-size: 14px;
        }

        span {
          color: #424242;
          font-size: 13px;
        }
      }
    }
  }

  .date-group {
    .start-date {
      padding-right: unset !important;

      @media (min-width: 1400px) {
        input {
          width: 75px !important;
        }
      }
    }

    .end-date {
      > label {
        width: 75px;
      }
    }
  }

  .generate-button-group {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    .dx-widget.dx-collection {
      > .dx-box {
        flex-direction: row !important;
        gap: 20px;
      }
    }

    .btn-clear {
      padding-top: 5px !important;
      gap: 20px;
    }
  }

  .dx-tagbox:not(.dx-texteditor-empty):not(.dx-editor-filled):not(
      .dx-editor-outlined
    )::after {
    border-bottom: 1px solid rgba(0, 0, 0, 0.42) !important;
  }

  .gc-grid-footer-css-v2 {
    .gc-grid-footer {
      height: 40px;
    }
  }

  .dx-master-detail-row {
    .dx-datagrid.dx-gridbase-container .dx-datagrid-header-panel {
      display: none !important;
    }

    > .dx-master-detail-cell {
      padding-left: 10px !important;
      padding-right: 0px !important;
    }
  }

  .custom-complete {
    display: flex;
    justify-content: center;

    .primary-complete {
      background-color: var(--Colors-Background-bg-secondary, #f9fafb);

      // width: 30px;
      display: flex;
      border-radius: 8px;
      // height: 20px;
      vertical-align: middle;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--Colors-Border-border-primary, #d0d5dd);

      width: 30px;
      height: 22px;
      opacity: 1;
      gap: 4;
      border-width: 1px;
      padding-top: spacing-xxs;
      padding-right: 16px;
      padding-bottom: spacing-xxs;
      padding-left: 16px;
      border-radius: radius-full;
    }
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    > td:first-of-type:not(.dx-command-expand) {
    padding-left: 10px !important;
  }
}
</style>
